// PR-P0-5 reference: UC-5 (Questionnaires — vector match + LLM re-rank) · ADR-0001 · docs/feature-status-and-gaps.md §2.5
// Mockup: .claude/skills/ux-adversary/runs/uc-5-questionnaires/
// Systems Architecture: §5.13, §1.2 (Vectorize binding)
import { QuestionnaireService } from './questionnaire-service.js';
import { defineTemplate, derive, field } from '../egress/data-classes.js';
import { buildLexicalIndex, lexicalRegisterMatch } from './questionnaire-lexical-matcher.js';
// Local RNG — same shape as questionnaire-service.
function cryptoRandom() {
    const g = globalThis;
    if (typeof g === 'object' &&
        g !== null &&
        'crypto' in g &&
        typeof g.crypto?.randomUUID ===
            'function') {
        return g.crypto
            .randomUUID()
            .replace(/-/g, '')
            .slice(0, 16);
    }
    return Math.random().toString(36).slice(2, 18);
}
// Per plan §5, topK=10 similarity candidates go to the LLM re-rank.
export const MATCH_TOP_K = 10;
// Re-rank up to N questions per LLM call. Small batches keep the prompt
// short and the schema simple; the batch size also caps total-model-call
// count at (questionCount / N) which is the main cost lever.
export const RERANK_BATCH_SIZE = 5;
// Keep at most this many citations per answer (the model returns 1-3
// per plan D5; enforce a hard cap in case it misbehaves).
export const MAX_CITATIONS_PER_ANSWER = 3;
// Match candidates whose relevance falls below this drop to the D4
// "no match" path. The value is intentionally generous — the confidence
// on the answer record (below) is what drives the review-UI badge.
export const MIN_RELEVANCE_KEPT = 0.15;
// Confidence bands derived from the average relevance of the top
// citations. See plan D5.
const HIGH_MIN = 0.6;
const MED_MIN = 0.35;
/**
 * Prompt + schema for the LLM re-rank call. Instructs the model to pick
 * 1-3 items per question with confidence and a short `why`. Kept as a
 * const so tests can lock it.
 */
export const RERANK_INSTRUCTIONS = [
    'You are a compliance analyst matching auditor questions to register items.',
    'For each question in the input, pick the 1-3 register items that best answer it.',
    'Return `relevance` in [0, 1] (1 = perfect match, 0 = irrelevant), `why` as a',
    'one-sentence justification the reviewer can eyeball.',
    'If no candidate is a reasonable match for a question, return an empty citations array for it.',
    'Do not invent risk_item_ids — pick only from the candidates listed under that question.',
].join(' ');
/** ADR-0073: reviewed static template; the batch payload (questions + candidates) is the classified input. */
export const RERANK_TEMPLATE = defineTemplate('questionnaire.match_rerank', 'v1', RERANK_INSTRUCTIONS, []);
export const RERANK_SCHEMA = {
    results: [
        {
            question_id: 'string',
            citations: [
                { risk_item_id: 'string', relevance: '0..1', why: 'short string' },
            ],
        },
    ],
};
/**
 * QuestionnaireMatcher — the P8 M3 match stage.
 *
 * Reads questions from a questionnaire in status='matching', embeds each,
 * runs Vectorize topK=10, then LLM re-ranks in batches of RERANK_BATCH_SIZE
 * questions per call. Persists an `questionnaire_answers` row per question
 * (with citations_json but empty draft_text — draft generation is M4) and
 * advances the questionnaire to status='drafting'.
 *
 * No-match (D4): questions whose top candidates all fall below
 * MIN_RELEVANCE_KEPT get an empty citations array, `draft_confidence='low'`,
 * and `needs_human_review=1`. The route surfaces the no-match count in the
 * response so the caller can render the "n of m need human review" header.
 */
export class QuestionnaireMatcher {
    db;
    llm;
    index;
    constructor(db, llm, index) {
        this.db = db;
        this.llm = llm;
        this.index = index;
    }
    /**
     * ADR-0073 parity (questionnaire_matching → deterministic): set once the gateway
     * refuses to embed customer question text (federal mode, a raised class, or a
     * policy without the index's embedding provider). Matching then runs lexically
     * over the public register text, in the Worker, with no model call.
     */
    lexical = null;
    embedRefused = false;
    /** How the last run matched: 'vector' (embed + Vectorize) or 'lexical' (deterministic). */
    matchMode = 'vector';
    async process(questionnaireId) {
        const svc = new QuestionnaireService(this.db);
        const row = await svc.get(questionnaireId);
        if (!row)
            throw new Error(`Matcher: no questionnaire ${questionnaireId}`);
        if (row.status !== 'matching') {
            throw new Error(`Matcher: questionnaire ${questionnaireId} is in status ${row.status}, not 'matching'`);
        }
        const questions = await this.loadQuestions(questionnaireId);
        if (questions.length === 0) {
            // Nothing to match — advance to drafting so the pipeline doesn't
            // stall. Reviewer will see zero questions in the M5 review UI and
            // can delete or re-ingest.
            await svc.updateStatus(questionnaireId, 'drafting');
            return { answers: [], no_match_count: 0 };
        }
        // Embed + topK for every question. Embeddings run one-per-question
        // (Workers AI accepts arrays but per-call errors bubble more cleanly
        // when they're isolated).
        const perQuestionCandidates = new Map();
        for (const q of questions) {
            const candidates = await this.embedAndQuery(q.text);
            perQuestionCandidates.set(q.question_id, candidates);
        }
        // Batched LLM re-rank. Skip the LLM entirely for questions whose topK
        // was empty — cheaper and matches the no-match D4 disclaimer path.
        const rerankable = questions.filter((q) => (perQuestionCandidates.get(q.question_id) ?? []).length > 0);
        const rerankedByQuestion = new Map();
        for (let i = 0; i < rerankable.length; i += RERANK_BATCH_SIZE) {
            const batch = rerankable.slice(i, i + RERANK_BATCH_SIZE);
            const batchResults = await this.rerankBatch(batch, perQuestionCandidates);
            for (const [qid, cites] of batchResults) {
                rerankedByQuestion.set(qid, cites);
            }
        }
        // Build the answer rows.
        const answers = questions.map((q) => {
            const cites = (rerankedByQuestion.get(q.question_id) ?? [])
                .filter((c) => c.relevance >= MIN_RELEVANCE_KEPT)
                .slice(0, MAX_CITATIONS_PER_ANSWER);
            const confidence = confidenceFromCitations(cites);
            const noMatch = cites.length === 0;
            return {
                question_id: q.question_id,
                citations: cites,
                draft_confidence: confidence,
                // D4 + plan §2 rule: needs_human_review is 1 for low confidence or
                // no citations at all.
                needs_human_review: noMatch || confidence === 'low',
            };
        });
        await this.persistAnswers(questionnaireId, answers);
        await svc.updateStatus(questionnaireId, 'drafting');
        const noMatchCount = answers.filter((a) => a.citations.length === 0).length;
        return { answers, no_match_count: noMatchCount, match_mode: this.matchMode };
    }
    // ---- internals -----------------------------------------------------
    async loadQuestions(questionnaireId) {
        const res = await this.db.query(`SELECT qq.*
         FROM questionnaire_questions qq
         JOIN questionnaires q
           ON q.questionnaire_id = qq.questionnaire_id
        WHERE q.workspace_id = ? AND qq.questionnaire_id = ?
        ORDER BY qq.seq_no`, [this.db.workspaceId, questionnaireId]);
        return res.results;
    }
    async embedAndQuery(text) {
        if (!this.embedRefused && this.index) {
            let vector;
            try {
                const r = await this.llm.embed({ task: 'questionnaire.match_embed', text: field('questionnaire.question_text', text) });
                if (r.status === 'ok') {
                    vector = r.vector;
                    if (!Array.isArray(vector) || vector.length === 0)
                        return [];
                    const matches = await this.index.query(vector, { topK: MATCH_TOP_K, returnMetadata: true });
                    return matches.map(candidateFromMatch);
                }
                this.embedRefused = true;
            }
            catch {
                return [];
            }
        }
        // Deterministic lexical match over the public register (no model, no Vectorize).
        this.matchMode = 'lexical';
        if (!this.lexical)
            this.lexical = buildLexicalIndex(await this.loadRegisterForLexical());
        return lexicalRegisterMatch(text, this.lexical, MATCH_TOP_K);
    }
    async loadRegisterForLexical() {
        const res = await this.db.unscoped().query(`SELECT item_id, title, description, framework_refs FROM risk_items WHERE status = 'active'`, []);
        return res.results.map((r) => {
            let frameworks = [];
            try {
                const p = JSON.parse(r.framework_refs ?? '[]');
                if (Array.isArray(p))
                    frameworks = p.map(String);
            }
            catch {
                frameworks = [];
            }
            return { risk_item_id: r.item_id, title: r.title, text: r.description ?? '', frameworks };
        });
    }
    async rerankBatch(batch, perQuestion) {
        const out = new Map();
        // Similarity-order top-3 per question when the gateway refuses the re-rank
        // (crude but keeps the pipeline moving; a person reviews every answer).
        const similarityOnly = () => {
            const why = this.matchMode === 'lexical' ? 'Keyword match (no AI re-rank).' : 'Similarity match (no LLM re-rank).';
            for (const q of batch) {
                const cites = (perQuestion.get(q.question_id) ?? [])
                    .slice(0, MAX_CITATIONS_PER_ANSWER)
                    .map((c) => ({ risk_item_id: c.risk_item_id, relevance: c.score, why }));
                out.set(q.question_id, cites);
            }
            return out;
        };
        if (batch.length === 0)
            return out;
        // Build the batched prompt payload. Each entry names its allowed
        // candidate set so the model can't fabricate risk_item_ids across
        // questions.
        const payload = batch.map((q) => ({
            question_id: q.question_id,
            question: q.text,
            section: q.section,
            candidates: (perQuestion.get(q.question_id) ?? []).map((c) => ({
                risk_item_id: c.risk_item_id,
                title: c.title,
                frameworks: c.frameworks,
                similarity: Number(c.score.toFixed(3)),
            })),
        }));
        const rerank = await this.llm.run({
            task: 'questionnaire.match_rerank', template: RERANK_TEMPLATE,
            input: derive('questionnaire.match_payload', JSON.stringify(payload), ['questionnaire.question_text', 'register.risk_item_text']),
            schemaHint: RERANK_SCHEMA,
        });
        if (rerank.status !== 'ok')
            return similarityOnly();
        const results = Array.isArray(rerank.output.results) ? rerank.output.results : [];
        const allowedByQuestion = new Map();
        for (const q of batch) {
            const ids = new Set((perQuestion.get(q.question_id) ?? []).map((c) => c.risk_item_id));
            allowedByQuestion.set(q.question_id, ids);
        }
        for (const r of results) {
            const qid = typeof r.question_id === 'string' ? r.question_id : null;
            if (!qid || !allowedByQuestion.has(qid))
                continue;
            const allowed = allowedByQuestion.get(qid);
            const rawCites = Array.isArray(r.citations) ? r.citations : [];
            const cites = [];
            for (const c of rawCites) {
                const cc = c;
                if (typeof cc.risk_item_id !== 'string' || !allowed.has(cc.risk_item_id))
                    continue;
                const relevance = typeof cc.relevance === 'number' ? Math.max(0, Math.min(1, cc.relevance)) : 0.5;
                const why = typeof cc.why === 'string' ? cc.why : '';
                cites.push({ risk_item_id: cc.risk_item_id, relevance, why });
            }
            out.set(qid, cites);
        }
        // Questions the LLM omitted from the response: no-match by omission.
        for (const q of batch) {
            if (!out.has(q.question_id))
                out.set(q.question_id, []);
        }
        return out;
    }
    async persistAnswers(questionnaireId, answers) {
        const now = new Date().toISOString();
        for (const a of answers) {
            // Same INSERT ... SELECT-with-workspace-check trick as the extractor.
            const res = await this.db.execute(`INSERT INTO questionnaire_answers (
            answer_id, questionnaire_id, question_id,
            draft_text, draft_confidence, drafted_by,
            citations_json, preparer_text,
            preparer_edited_at, preparer_edited_by_user_id,
            approved_at, approved_by_user_id,
            needs_human_review, created_at, updated_at
          )
          SELECT ?, questionnaire_id, ?, NULL, ?, NULL, ?, NULL, NULL, NULL, NULL, NULL, ?, ?, ?
            FROM questionnaires
           WHERE questionnaire_id = ? AND workspace_id = ?`, [
                `qa-${cryptoRandom()}`,
                a.question_id,
                a.draft_confidence,
                JSON.stringify(a.citations),
                a.needs_human_review ? 1 : 0,
                now,
                now,
                questionnaireId,
                this.db.workspaceId,
            ]);
            if (!res.meta?.changes) {
                throw new Error(`Matcher.persistAnswers: questionnaire ${questionnaireId} not in workspace ${this.db.workspaceId} (or deleted mid-run)`);
            }
        }
    }
}
function candidateFromMatch(m) {
    const metadata = m.metadata ?? {};
    const rawFrameworks = metadata['frameworks'];
    const frameworks = Array.isArray(rawFrameworks) ? rawFrameworks.map(String) : undefined;
    const title = typeof metadata['title'] === 'string' ? metadata['title'] : undefined;
    return { risk_item_id: m.id, score: m.score, title, frameworks };
}
export function confidenceFromCitations(cites) {
    if (cites.length === 0)
        return 'low';
    const avg = cites.reduce((s, c) => s + c.relevance, 0) / cites.length;
    if (avg >= HIGH_MIN)
        return 'high';
    if (avg >= MED_MIN)
        return 'medium';
    return 'low';
}
//# sourceMappingURL=questionnaire-matcher-service.js.map